import styled from '@emotion/styled'
import React, { useCallback, useMemo } from 'react'

import { FormValues } from './RulesForm'
import RulesForm from './RulesForm'
import TestRoutingRuleSection from './TestRoutingRuleSection'
import ConfiguratorActionButtons from 'components/ConfiguratorActionButtons'
import { Portal } from 'components/Portal'
import SlideInWrapper from 'components/SlideInWrapper'
import UnsavedChangesModal from 'components/UnsavedChangesModal'
import DeleteRuleModal from 'components/advanced-settings/rules/DeleteRuleModal'
import { useFormState } from 'hooks/useFormState'
import { useOrganization } from 'hooks/useOrganization'
import { useRules } from 'hooks/useRules'
import { useTemplates } from 'hooks/useTemplates'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { useUnsavedChangesModal } from 'hooks/useUnsavedChangesModal'
import { ActionButtonsPortalContext } from 'layouts/AdvancedSettingsLayout'
import { HeadingPortalContext } from 'layouts/AdvancedSettingsLayout'
import { deleteRule, updateOrganization, updateRulesOrder } from 'lib/api'
import { useAppStore } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
`

function RulesSection() {
	const { rules, isLoading: isRulesLoading, mutateRules } = useRules()
	const { templates, areTemplatesLoading } = useTemplates()
	const { organization, mutateOrganization, isOrganizationLoading } = useOrganization()
	const store = useAppStore()
	const formId = 'rules-form'
	const [state, handlers] = useFormState()
	const formRef = React.useRef<HTMLFormElement>(null)
	const { hideModal, handleDiscardChanges } = useUnsavedChangesModal(formRef, state.isDirty)
	const showToast = useToast()
	const track = useTracking()
	const isLoading = isRulesLoading || areTemplatesLoading || isOrganizationLoading

	const [ruleIdToDelete, setRuleIdToDelete] = React.useState<string | null>(null)
	const [highlightedRuleId, setHighlightedRuleId] = React.useState<string | null>(null)

	const handleShowDeleteModal = useCallback(
		(id: string) => {
			setRuleIdToDelete(id)
			store.dispatch({ type: 'currentModal/set', payload: 'ConfirmDisableModal' })
		},
		[store],
	)

	const handleHideDeleteModal = useCallback(() => {
		setRuleIdToDelete(null)
		store.dispatch({ type: 'currentModal/set', payload: null })
	}, [store])

	const handleDeleteRule = useCallback(async () => {
		if (!ruleIdToDelete) {
			return
		}

		try {
			await deleteRule(ruleIdToDelete)
			await mutateRules((current) => current?.filter((value) => value.id !== ruleIdToDelete))
			track(EVENT_NAMES.RULE_REMOVED)
			showToast('success', 'Rule deleted')
		} catch {
			showToast('error', 'Failed to delete rule')
		} finally {
			handleHideDeleteModal()
		}
	}, [ruleIdToDelete, mutateRules, handleHideDeleteModal, showToast, track])

	const handleSave = useCallback(
		async (formValues: FormValues) => {
			if (!state.isDirty) {
				return
			}

			try {
				const updates = {
					rules: JSON.stringify(rules?.map((rule) => rule.id)) !== JSON.stringify(formValues.reorderedRules),
					template: organization?.fallbackTemplateId !== formValues.fallbackTemplateId,
				}

				if (updates.rules || updates.template) {
					const updatePromises: Promise<unknown>[] = []

					if (updates.rules) {
						updatePromises.push(updateRulesOrder(formValues.reorderedRules))
					}

					if (updates.template) {
						updatePromises.push(updateOrganization({ fallbackTemplateId: formValues.fallbackTemplateId }))
					}

					await Promise.all(updatePromises)

					if (updates.rules) {
						await mutateRules()
						track(EVENT_NAMES.RULES_REORDERED)
					}
					if (updates.template) {
						await mutateOrganization()
						track(EVENT_NAMES.RULES_FALLBACK_TEMPLATE_CHANGED)
					}

					showToast('success')
				}
			} catch {
				showToast('error')
			}
		},
		[state.isDirty, rules, organization?.fallbackTemplateId, mutateRules, mutateOrganization, showToast, track],
	)

	const formProps = useMemo(
		() => ({
			formId,
			rules: rules ?? [],
			templates: templates ?? [],
			fallbackTemplateId: organization?.fallbackTemplateId ?? '',
			onSave: handleSave,
			onDelete: handleShowDeleteModal,
			formRef,
			onFormStateChange: handlers,
			highlightedRuleId,
		}),
		[
			rules,
			templates,
			organization?.fallbackTemplateId,
			handleSave,
			handleShowDeleteModal,
			handlers,
			highlightedRuleId,
		],
	)

	if (isLoading) {
		return null
	}

	return (
		<>
			<UnsavedChangesModal onDiscardChanges={handleDiscardChanges} onStayHere={hideModal} name="Rules" />
			{ruleIdToDelete && <DeleteRuleModal onClose={handleHideDeleteModal} onDelete={handleDeleteRule} />}
			<SlideInWrapper axis="y" fullHeight>
				<Portal context={HeadingPortalContext}>
					{rules && rules.length > 0 && templates && (
						<TestRoutingRuleSection templates={templates} localRules={rules} onHighlightRule={setHighlightedRuleId} />
					)}
				</Portal>
				<ContentWrapper>
					<RulesForm {...formProps} />
					<ConfiguratorActionButtons formId={formId} {...state} context={ActionButtonsPortalContext} />
				</ContentWrapper>
			</SlideInWrapper>
		</>
	)
}

export default RulesSection
